// src/api/attendance.js
import http from "./apiClient";
import { parseToDate } from "../utils/dateUtils";

const RESOURCE_PATH = "/attendance";

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value == null || value === "") return [];
  return [value];
};

const normalizeRecord = (record) => {
  if (!record || typeof record !== "object") return record;
  const status = record.status === "X" ? null : record.status;
  return { ...record, status };
};

const safeParseDate = (value) => {
  const parsed = parseToDate(value);
  if (!parsed) return null;
  // Normalise to midnight to make range comparisons easier
  return new Date(Date.UTC(parsed.getUTCFullYear(), parsed.getUTCMonth(), parsed.getUTCDate()));
};

const filterByRange = (records, { fromDate, toDate }) => {
  if (!fromDate && !toDate) return records;
  const from = fromDate ? safeParseDate(fromDate) : null;
  const to = toDate ? safeParseDate(toDate) : null;
  if (!from && !to) return records;

  return records.filter((record) => {
    const ref = record?.date || record?.attendanceDate || record?.day;
    const recordDate = safeParseDate(ref);
    if (!recordDate) return true;
    if (from && recordDate < from) return false;
    if (to && recordDate > to) return false;
    return true;
  });
};

const buildSearchParams = ({ childId, classroomId, date }) => {
  const params = {};
  if (childId) params.childId = childId;
  if (classroomId) params.classroomId = classroomId;
  if (date) params.date = date;
  return params;
};

const sanitizeStatus = (value) => {
  if (value == null || value === "") return "X";
  return value;
};

/**
 * Fetch attendance records filtered by optional child, classroom and date.
 * When a date range is provided, results are fetched once and filtered client-side.
 */
export const fetchAttendanceRecords = async ({ childId, classroomId, date, fromDate, toDate } = {}) => {
  if (!childId && !classroomId && !date && !fromDate && !toDate) {
    throw new Error("fetchAttendanceRecords: provide at least childId, classroomId or a date filter");
  }

  const singleDay = date ?? (fromDate && toDate && fromDate === toDate ? fromDate : null);

  try {
    const response = await http.get(RESOURCE_PATH, {
      params: buildSearchParams({ childId, classroomId, date: singleDay }),
      validateStatus: (status) => [200, 204].includes(status),
    });
    if (response.status === 204) return [];

    const records = toArray(response.data).map(normalizeRecord);
    if (!singleDay && (fromDate || toDate)) {
      return filterByRange(records, { fromDate, toDate });
    }
    return records;
  } catch (err) {
    // Laat calling code fout afhandelen
    throw err;
  }
};

/**
 * Create a new attendance record for a child.
 */
export const upsertAttendanceRecord = async ({
  classroomId,
  childId,
  attendanceDate,
  date,
  status,
}) => {
  const resolvedDate = attendanceDate ?? date;
  const resolvedStatus = sanitizeStatus(status);

  if (!classroomId || !childId || !resolvedDate) {
    throw new Error("upsertAttendanceRecord: classroomId, childId and attendanceDate are required");
  }

  const payload = {
    classroomId,
    childId,
    attendanceDate: resolvedDate,
    status: resolvedStatus,
  };
  const { data } = await http.post(RESOURCE_PATH, payload);
  return data;
};

/**
 * Update an existing attendance record.
 */
export const updateAttendanceRecord = async (recordId, updates = {}) => {
  if (!recordId) throw new Error("updateAttendanceRecord: recordId is required");

  const attendanceDate = updates.attendanceDate ?? updates.date;
  const resolvedStatus = sanitizeStatus(updates.status);
  const payload = {
    childId: updates.childId,
    classroomId: updates.classroomId,
    attendanceDate,
    status: resolvedStatus,
  };

  if (!payload.childId || !payload.classroomId || !payload.attendanceDate) {
    throw new Error("updateAttendanceRecord: missing required fields in payload");
  }

  const { data } = await http.put(`${RESOURCE_PATH}/${recordId}`, payload);
  return data;
};

/**
 * Convenience helper to replace or upsert multiple entries sequentially.
 */
export const bulkReplaceAttendance = async ({ entries }) => {
  if (!Array.isArray(entries)) {
    throw new Error("bulkReplaceAttendance: entries[] is required");
  }

  const results = [];
  for (const entry of entries) {
    const { id, recordId, ...rest } = entry || {};
    if (id || recordId) {
      const updated = await updateAttendanceRecord(id ?? recordId, rest);
      results.push(updated);
    } else {
      const created = await upsertAttendanceRecord(rest);
      results.push(created);
    }
  }
  return results;
};

/**
 * Fetch today's attendance context for the logged-in teacher.
 * Requires backend support for /attendance/today.
 */
export const fetchTodayAttendanceForTeacher = async ({ date } = {}) => {
  const params = {};
  if (date) params.date = date;
  const { data } = await http.get(`${RESOURCE_PATH}/today`, { params });
  return data;
};

/**
 * Submit the teacher's attendance updates for today.
 * Requires backend support for /attendance/today.
 */
export const submitTodayAttendance = async ({ classroomId, date, entries }) => {
  if (!classroomId || !Array.isArray(entries)) {
    throw new Error("submitTodayAttendance: classroomId and entries[] are required");
  }
  const payload = { classroomId, date, entries };
  const { data } = await http.post(`${RESOURCE_PATH}/today`, payload);
  return data;
};
